import {useMemo} from 'react';
import {useParams} from 'react-router';
import {Moment} from 'moment';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {DateRangeOnChange} from '../../../date-range-input/date-range-utils';
import {ScreenPaths} from '../../../../enums/screen-path';
import {useOffice} from '../../../../contexts/office/context';
import {useAuth} from '../../../../contexts/auth/context';
import {CareServicesHeader} from '../../base/header/care-services-header';
import {
  CareServicePreparedExportData,
  CareServiceCreatingItem,
  OnCareServiceExportPdfClick,
} from '../../base/data/care-service-item';
import {FetchEntityUsersResult} from '../../../../hooks/use-fetch-entity-users';

interface CaregiverCareServicesHeaderProps {
  startDate: Moment;
  endDate: Moment;
  filterUsers: FetchEntityUsersResult;
  canWrite?: boolean;
  onDateChange: DateRangeOnChange;
  onCreateItemClick: (newItem: CareServiceCreatingItem) => void;
  onFilterUserChange: (user: AffiliatedUser | undefined) => void;
  onExportPdfClick: OnCareServiceExportPdfClick;
  onExportXlsxClick?: (preparedData: CareServicePreparedExportData) => void;
}

export function CaregiverCareServicesHeader(props: CaregiverCareServicesHeaderProps) {
  const {url, entityId, id}: {url: string; entityId: string; id: string} = useParams();
  const officeCtx = useOffice();
  const authContext = useAuth();
  const {t} = useTranslation();

  const caregiver = officeCtx.state.userForm?.user;
  const caregiverName = caregiver?.displayName || caregiver?.name;
  const entity = authContext.state.session?.selectedAffiliation?.entity;

  const crumbs = useMemo(() => {
    return [
      {
        label: t(translations.screen_paths.professional_caregiver_team),
        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`,
      },
      {
        label: t(
          translations.office.professional_caregiver.profile.generic_header.breadcrumbs
            .professionals
        ),
        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`,
      },
      {
        label: caregiverName,
        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}/${caregiver?.id}`,
      },
      {
        label: t(translations.seniorSideMenu.careServices),
        link: url,
      },
    ];
  }, [caregiverName, caregiver?.id, entityId, t, url]);

  return (
    <CareServicesHeader
      isLoading={caregiver === undefined}
      crumbs={crumbs}
      startDate={props.startDate}
      endDate={props.endDate}
      canWrite={props.canWrite}
      onDateChange={props.onDateChange}
      onCreateItemClick={(item) => {
        // --- additional fields ---
        // add responsibleUserIds as the current caregiver id
        if (id) {
          item.responsibleUserIds = [id];
        }

        props.onCreateItemClick(item);
      }}
      filterUsers={props.filterUsers}
      filterDefaultOptionLabel={translations.planner.header.filter_all_seniors_label}
      onFilterUserChange={props.onFilterUserChange}
      onExportPdfClick={async () => {
        props.onExportPdfClick({
          startDate: props.startDate,
          endDate: props.endDate,
          user: caregiver,
          entity,
        });
      }}
      onExportXlsxClick={
        props.onExportXlsxClick
          ? () => {
              props.onExportXlsxClick?.({user: caregiver, entity});
            }
          : undefined
      }
    />
  );
}
